<template>
  <div class="q-mt-sm">
    <span
      class="q-pa-none text-grey-7"
      v-if="taskIs('merch', 'install') || taskIs('merch', 'remove')"
    >
      <q-item-section>
        {{ taskSpanDuration }}
      </q-item-section>
    </span>
    <q-list
      v-for="(row, rowIndex) in templateItemsRows"
      :key="rowIndex"
      class="row q-gutter-x-xl q-gutter-y-md items-start"
    >
      <template v-for="(item, itemIndex) in row" :key="itemIndex">
        <q-item v-if="item.value" class="q-pa-none">
          <q-item-section>
            <q-item-label overline class="text-grey-7">{{ item.label }}</q-item-label>
            <q-item-label><span v-html="item.value"></span></q-item-label>
          </q-item-section>
        </q-item>
      </template>
    </q-list>
    <q-list class="row q-gutter-x-xl q-gutter-y-md items-start" v-if="task.attachment">
      <q-item class="q-pa-none col-3">
        <q-item-section>
          <q-item-label overline class="text-grey-7">
            {{ taskProfileLabel('attachment') }}
          </q-item-label>
          <q-item-label>
            <TaskAttachment :attachment="task.attachment" />
          </q-item-label>
        </q-item-section>
      </q-item>
    </q-list>
  </div>
</template>
<script setup lang="ts">
import { computed } from 'vue'
import { useFormatDate } from '../composables/formatDate'
import { useFormatTime } from '../composables/formatTime'
import { useI18n } from 'vue-i18n'
import TaskAttachment from './TaskAttachment.vue'
import { Task } from '../store/useTaskProfiles'
import { merchIndexKeys, taskProfileIndexStructure } from './task_profile/taskProfileIndexStructure'
import { useCodes } from '../store/useCodes'
import { useUserStore } from '../store/useUserStore'
import { storeToRefs } from 'pinia'

const props = defineProps<{
  task: Task
}>()
//translations
const { t } = useI18n({ useScope: 'global' })
const taskProfileLabel = (key: string) => t(`taskProfile.${key}`)

/**
 * Returns label and value for items in the template's row
 * Some items (e.g. joined fields) with special
 * render rules are held here as well
 */
const templateRow = (keys: string[]) => {
  return keys.map((key) => {
    //Going to remove, may not need now
    // if (key == 'job_id_wave') {
    //   return {
    //     label: `${taskProfileLabel('job_id')} / ${taskProfileLabel('wave')}`,
    //     value: `${props.task.job_id} / ${props.task.wave_id}`,
    //   }
    // }
    if (key == 'desc_art_id') {
      if (!props.task.artwork || !props.task.artwork_id) {
        return {
          label: null,
          value: null,
        }
      }
      return {
        label: `${taskProfileLabel('artwork')} / ${taskProfileLabel('artwork_id')}`,
        value: `${props.task.artwork} / ${props.task.artwork_id}`,
      }
    }
    if (key == 'ad_qty_type') {
      return {
        label: taskProfileLabel('ad_qty_type'),
        value: `${props.task.ad_quantity} ${props.task.ad_type}`,
      }
    }
    if (key == 'special_instructions') {
      return {
        label: taskProfileLabel(key),
        value: props.task[key] ? `<strong><em><mark>${props.task[key]}</mark></em></strong>` : null,
      }
    }
    if (key == 'previous_placement_id' && props.task[key]) {
      if (!props.task[key] || !props.task[`placement_instruction${String(props.task[key])}`]) {
        return {
          label: null,
          value: null,
        }
      }
      return {
        label: taskProfileLabel(key),
        value: props.task[key]
          ? props.task[`placement_instruction${String(props.task[key])}`] +
            ` [${String(props.task[key])}]`
          : null,
      }
    }
    if (key == 'prev_rai_code_id') {
      const code = useCodes().getCode('rai_codes', 'id', props.task.prev_rai_code_id)
      const user = storeToRefs(useUserStore())
      let codeText
      if (code) {
        codeText = `${code['code']} - ${
          code[user.language.value == 'en_US' ? 'description_en' : 'description_fr']
        }`
      }
      return {
        label: 'Prev Rai Code',
        value: codeText,
      }
    }
    if (key == 'prev_item_installed' && props.task.prev_item_installed) {
      //this comes in as a stringified array so we need to set to json and get the part
      //need to do twice
      const partArray = JSON.parse(JSON.parse(props.task.prev_item_installed as string))
      if (partArray && partArray.length) {
        const partText = partArray
          .filter((part: any) => Number(part['quantity'] > 0))
          .map((part: any) => part['name_en'])
          .join(', ')
        return { label: taskProfileLabel(key), value: partText }
      }
    }
    if (key == 'previous_execution_timestamp_tz' && props.task.previous_execution_timestamp_tz) {
      const date = new Date(props.task[key] as string)
      return {
        label: taskProfileLabel(key),
        value: date?.toLocaleString(),
      }
    }
    if (key == 'task_status') {
      return {
        label: taskProfileLabel(key),
        value: {
          Open: { en_US: 'Open', fr_CA: 'Ouvert', es_MX: '' },
          Completed: { en_US: 'Completed', fr_CA: 'Fermé', es_MX: '' },
        }[props.task[key]][user.language.value],
      }
    }
    return { label: taskProfileLabel(key), value: props.task[key] }
  })
}
const user = storeToRefs(useUserStore())
/**
 * Helper fn to determine if task is work/type combo
 */
const taskIs = (work: string, type: string): boolean =>
  String(props.task.work_type).toLowerCase().replaceAll(' ', '') === work.replaceAll(' ', '') &&
  String(props.task.task_type).toLowerCase().replaceAll(' ', '') === type.replaceAll(' ', '')
/**
 * Computes the tasks start/end times and formats it
 */
const taskSpan = computed(() => {
  const start = useFormatDate(new Date(props.task.task_start_date))
  const end = useFormatDate(new Date(props.task.task_end_date))
  return `${start.weekday()}, ${start.month()} ${start.day()} → ${end.weekday()}, ${end.month()} ${end.day()}`
})
/**
 * Returns formated task duration
 */
const taskDuration = computed(() => {
  return `${useFormatTime(Number(props.task.est_task_duration))} ${taskProfileLabel(
    'est_task_duration'
  )}`
})

const taskSpanDuration = computed(() => `${taskSpan.value} / ~${taskDuration.value}`)

/**
 * This object determines rendered items
 */
const templateItemsRows = computed(() => {
  //set a default template
  let data = [
    'placement_section',
    'task',
    'product_line',
    'desc_art_id',
    'order_sales_id',
    'task_status',
    'space_id',
    'previous_placement_id',
    'prev_rai_code_id',
    'previous_ad_quantity',
    'special_instructions',
    'previous_execution_timestamp_tz',
  ]
  console.log(
    `order type id: ${props.task.order_type_id}, task type id: ${props.task.task_type_id}, product line id: ${props.task.product_line_id}`
  )
  //merch always has the same keys, regardless of task type id or product line
  if (props.task.order_type_id == '4') {
    console.log('merch template found')
    data = merchIndexKeys
  } else if (
    //now check for instore/cart, but lets make sure all keys exist first
    //@ts-expect-error its a check, will return false if not here
    taskProfileIndexStructure[Number(props.task.order_type_id)] &&
    //@ts-expect-error its a check, will return false if not here
    taskProfileIndexStructure[Number(props.task.order_type_id)][Number(props.task.task_type_id)] &&
    //@ts-expect-error its a check, will return false if not here
    taskProfileIndexStructure[Number(props.task.order_type_id)][Number(props.task.task_type_id)][
      Number(props.task.product_line_id)
    ]
  ) {
    console.log('task template found')
    data =
      //@ts-expect-error task type will be same as keys
      taskProfileIndexStructure[Number(props.task.order_type_id)][Number(props.task.task_type_id)][
        Number(props.task.product_line_id)
      ]
  } else {
    console.log('task template not found')
    //@ts-expect-error its a check, will return false if not here
    if (!taskProfileIndexStructure[Number(props.task.order_type_id)]) {
      console.log(`order type id not in template: ${props.task.order_type_id}`)
    } else if (
      // @ts-expect-error its a check, will return false if not here
      !taskProfileIndexStructure[Number(props.task.order_type_id)][Number(props.task.task_type_id)]
    ) {
      console.log(`task type id not in template: ${props.task.task_type_id}`)
    } else {
      console.log(`product line id not in template: ${props.task.product_line_id}`)
    }
    console.log('using backup template')
  }

  return [templateRow(data)]
})
</script>
